import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, GripVertical, PanelTop, Plus, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@slumbr/ui";
import type { DockWidget } from "@slumbr/app-sdk";

import { useOpenAppsStore } from "@/stores/open-apps-store";
import {
  useDockStore,
  DOCK_POSITION_LABELS,
  type DockPosition,
} from "./dock-store";
import { useVisibleWidgets } from "./use-dock";
import { DockErrorBoundary } from "./dock-error-boundary";

/** Where each corner anchors. */
const CORNER_CLASSES: Record<Exclude<DockPosition, "appbar">, string> = {
  "bottom-right": "bottom-4 right-4 items-end",
  "bottom-left": "bottom-4 left-4 items-start",
  "top-right": "right-4 top-20 items-end",
  "top-left": "left-4 top-20 items-start",
};

/** Corner the pointer is closest to, used while dragging. */
function nearestCorner(x: number, y: number): Exclude<DockPosition, "appbar"> {
  const right = x > window.innerWidth / 2;
  const bottom = y > window.innerHeight / 2;
  if (bottom) return right ? "bottom-right" : "bottom-left";
  return right ? "top-right" : "top-left";
}

/** Top strip of the window — dropping here snaps the dock into the app bar. */
function isOverAppBar(y: number): boolean {
  return y < 64;
}

/** Desktop quick-actions dock: a launcher pill + stacked floating panels. */
export function DockDesktop() {
  const widgets = useVisibleWidgets();
  const openPanels = useDockStore((s) => s.openPanels);
  const togglePanel = useDockStore((s) => s.togglePanel);
  const closePanel = useDockStore((s) => s.closePanel);
  const position = useDockStore((s) => s.position);
  const setPosition = useDockStore((s) => s.setPosition);
  const smartPosition = useDockStore((s) => s.smartPosition);
  const setSmartPosition = useDockStore((s) => s.setSmartPosition);
  const applyAppPosition = useDockStore((s) => s.applyAppPosition);
  const activeAppId = useOpenAppsStore((s) => s.activeAppId);

  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Smart mode: each app gets the spot you last left the dock in.
  useEffect(() => {
    applyAppPosition(activeAppId ?? null);
  }, [activeAppId, applyAppPosition, smartPosition]);

  const move = useCallback(
    (next: DockPosition) => setPosition(next, activeAppId ?? null),
    [setPosition, activeAppId]
  );

  // Only render panels for widgets that are still visible.
  const visibleIds = new Set(widgets.map((w) => w.id));
  const panels = openPanels
    .filter((id) => visibleIds.has(id))
    .map((id) => widgets.find((w) => w.id === id)!)
    .filter(Boolean);

  if (widgets.length === 0) return null;

  // Docked into the app bar: TopBar renders the launcher, we only float the
  // open panels beneath it.
  if (position === "appbar") {
    return (
      <>
        {panels.length > 0 && (
          <div className="pointer-events-none fixed right-4 top-[68px] z-[120] flex flex-col items-end gap-2">
            {panels.map((w) => (
              <DockPanel key={w.id} widget={w} onClose={() => closePanel(w.id)} />
            ))}
          </div>
        )}
        <DragGhost drag={drag} />
      </>
    );
  }

  const anchor = CORNER_CLASSES[position];
  const stacksUp = position.startsWith("bottom");

  return (
    <>
      <div
        className={cn(
          "pointer-events-none fixed z-[120] flex gap-2",
          anchor,
          stacksUp ? "flex-col" : "flex-col-reverse"
        )}
      >
        {/* Panels stack away from the pill so it always stays on the outside. */}
        <div
          className={cn(
            "flex gap-2",
            stacksUp ? "flex-col-reverse" : "flex-col",
            position.endsWith("right") ? "items-end" : "items-start"
          )}
        >
          {panels.map((w) => (
            <DockPanel key={w.id} widget={w} onClose={() => closePanel(w.id)} />
          ))}
        </div>

        <DockLauncher
          widgets={widgets}
          openPanels={openPanels}
          onToggle={togglePanel}
          onDragMove={setDrag}
          onDragEnd={(x, y) => {
            setDrag(null);
            move(isOverAppBar(y) ? "appbar" : nearestCorner(x, y));
          }}
          menuOpen={menuOpen}
          onMenuOpenChange={setMenuOpen}
          position={position}
          onPick={move}
          smartPosition={smartPosition}
          onToggleSmart={() => setSmartPosition(!smartPosition)}
        />
      </div>

      <DragGhost drag={drag} />
    </>
  );
}

/** The launcher pill itself — reused by the app-bar mount. */
export function DockLauncher({
  widgets,
  openPanels,
  onToggle,
  onDragMove,
  onDragEnd,
  menuOpen,
  onMenuOpenChange,
  position,
  onPick,
  smartPosition,
  onToggleSmart,
  compact,
}: {
  widgets: DockWidget[];
  openPanels: string[];
  onToggle: (id: string) => void;
  onDragMove?: (point: { x: number; y: number } | null) => void;
  onDragEnd?: (x: number, y: number) => void;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  position: DockPosition;
  onPick: (next: DockPosition) => void;
  smartPosition: boolean;
  onToggleSmart: () => void;
  /** App-bar mount: no drag handle, tighter padding, square corners. */
  compact?: boolean;
}) {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number } | null>(null);

  // Position the portalled menu against the launcher, flipping to whichever
  // side has room.
  useLayoutEffect(() => {
    if (!menuOpen) return;
    const anchor = anchorRef.current?.getBoundingClientRect();
    if (!anchor) return;
    const width = 224;
    const height = 320;
    const openDown = anchor.bottom + height + 8 < window.innerHeight;
    const left = Math.min(
      Math.max(8, anchor.right - width),
      window.innerWidth - width - 8
    );
    const top = openDown ? anchor.bottom + 8 : Math.max(8, anchor.top - height - 8);
    setMenuPos({ left, top });
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      onMenuOpenChange(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onMenuOpenChange(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, onMenuOpenChange]);

  const startDrag = (event: React.PointerEvent) => {
    if (!onDragEnd) return;
    event.preventDefault();
    const onMove = (e: PointerEvent) => onDragMove?.({ x: e.clientX, y: e.clientY });
    const onUp = (e: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onDragMove?.(null);
      onDragEnd(e.clientX, e.clientY);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div
      ref={anchorRef}
      className={cn(
        "pointer-events-auto relative flex items-center gap-1",
        compact
          ? "rounded-lg bg-white/[0.05] p-1 ring-1 ring-inset ring-white/[0.07]"
          : "rounded-2xl bg-[#12161b]/90 p-1.5 shadow-[0_16px_40px_-14px_rgba(0,0,0,0.95)] ring-1 ring-inset ring-white/[0.09] backdrop-blur-2xl"
      )}
    >
      {!compact && onDragEnd && (
        <span
          onPointerDown={startDrag}
          title="Drag to move"
          className="flex h-9 w-5 cursor-grab items-center justify-center rounded-lg text-[#5f6b78] transition hover:bg-white/[0.06] hover:text-[#c3c9d0] active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </span>
      )}

      {widgets.map((w) => (
        <DockIcon
          key={w.id}
          widget={w}
          active={openPanels.includes(w.id)}
          onClick={() => onToggle(w.id)}
        />
      ))}

      {widgets.length > 0 && <span className="mx-0.5 h-5 w-px shrink-0 bg-white/[0.08]" />}

      <button
        type="button"
        onClick={() => onMenuOpenChange(!menuOpen)}
        title="Dock options"
        aria-label="Dock options"
        className={cn(
          "flex h-9 w-9 items-center justify-center rounded-xl transition",
          menuOpen
            ? "bg-white/[0.1] text-white"
            : "text-[#8b93c7] hover:bg-white/[0.07] hover:text-white"
        )}
      >
        <Plus className="h-4 w-4" />
      </button>

      {menuOpen && menuPos && createPortal(
        <div
          ref={menuRef}
          style={{ left: menuPos.left, top: menuPos.top }}
          className="fixed z-[400] w-56 overflow-hidden rounded-2xl border border-white/[0.08] bg-[#14171c]/97 py-1 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.85)] backdrop-blur-xl"
        >
          <p className="px-3 pb-1 pt-2 text-[10px] font-black uppercase tracking-[0.18em] text-[#536471]">
            Dock position
          </p>
          {(Object.keys(DOCK_POSITION_LABELS) as DockPosition[]).map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                onPick(id);
                onMenuOpenChange(false);
              }}
              className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-[13px] text-[#d7dbdc] transition hover:bg-white/[0.06]"
            >
              {id === "appbar" ? (
                <PanelTop className="h-3.5 w-3.5 text-[#71767b]" />
              ) : (
                <span className="h-3.5 w-3.5" />
              )}
              <span className="flex-1">{DOCK_POSITION_LABELS[id]}</span>
              {position === id && <Check className="h-3.5 w-3.5 text-[#ff8fac]" />}
            </button>
          ))}

          <div className="mx-2 my-1 border-t border-white/[0.06]" />

          <button
            type="button"
            onClick={onToggleSmart}
            className="flex w-full items-start gap-2.5 px-3 py-2 text-left transition hover:bg-white/[0.06]"
          >
            <Sparkles
              className={cn(
                "mt-0.5 h-3.5 w-3.5 shrink-0",
                smartPosition ? "text-[#ff8fac]" : "text-[#71767b]"
              )}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold text-[#d7dbdc]">
                Smart position
              </span>
              <span className="block text-[11px] leading-4 text-[#71767b]">
                Remember a spot per app and move there automatically.
              </span>
            </span>
            <span
              className={cn(
                "relative mt-0.5 h-4 w-7 shrink-0 rounded-full transition-colors",
                smartPosition ? "bg-[#ff477e]" : "bg-white/[0.14]"
              )}
            >
              <span
                className={cn(
                  "absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform",
                  smartPosition ? "translate-x-[14px]" : "translate-x-0.5"
                )}
              />
            </span>
          </button>

          <div className="mx-2 my-1 border-t border-white/[0.06]" />

          <button
            type="button"
            onClick={() => {
              onMenuOpenChange(false);
              navigate("/settings?tab=quick-actions");
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-[#d7dbdc] transition hover:bg-white/[0.06]"
          >
            <Plus className="h-3.5 w-3.5 text-[#71767b]" />
            Manage quick actions
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

/** Follows the pointer during a drag so the drop target is obvious. */
function DragGhost({ drag }: { drag: { x: number; y: number } | null }) {
  if (!drag) return null;
  const snapping = isOverAppBar(drag.y)
    ? "Snap into the app bar"
    : DOCK_POSITION_LABELS[nearestCorner(drag.x, drag.y)];
  return (
    <div
      className="pointer-events-none fixed z-[130] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#14171c]/95 px-3 py-1.5 text-[11px] font-bold text-[#e7e9ea] ring-1 ring-inset ring-white/[0.12] backdrop-blur"
      style={{ left: drag.x, top: drag.y - 28 }}
    >
      {snapping}
    </div>
  );
}

function DockIcon({
  widget,
  active,
  onClick,
}: {
  widget: DockWidget;
  active: boolean;
  onClick: () => void;
}) {
  const count = widget.badge?.();
  return (
    <button
      type="button"
      onClick={onClick}
      title={widget.title}
      className={cn(
        "relative flex h-9 w-9 items-center justify-center rounded-xl text-base transition",
        active
          ? "bg-[#ff477e]/20 text-[#ff8fac] ring-1 ring-inset ring-[#ff477e]/30"
          : "text-[#d7dbdc] hover:bg-white/[0.07]"
      )}
    >
      {widget.icon}
      {count != null && count > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#ff477e] px-1 text-[9px] font-black text-white">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

function DockPanel({ widget, onClose }: { widget: DockWidget; onClose: () => void }) {
  const Body = widget.PanelUI;
  return (
    <div className="pointer-events-auto flex max-h-[72vh] w-[360px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#101118] shadow-[0_12px_40px_-10px_rgba(0,0,0,0.8)]">
      <div className="flex items-center justify-between border-b border-white/[0.06] px-3 py-2">
        <span className="flex items-center gap-2 text-sm font-black text-white">
          <span className="text-base">{widget.icon}</span>
          {widget.title}
        </span>
        <button type="button" onClick={onClose} className="text-[#8b93c7] transition hover:text-white">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <DockErrorBoundary>
          <Body />
        </DockErrorBoundary>
      </div>
    </div>
  );
}

/** Launcher mounted inside the app bar when the dock is snapped there. */
export function DockInAppBar() {
  const widgets = useVisibleWidgets();
  const openPanels = useDockStore((s) => s.openPanels);
  const togglePanel = useDockStore((s) => s.togglePanel);
  const position = useDockStore((s) => s.position);
  const setPosition = useDockStore((s) => s.setPosition);
  const smartPosition = useDockStore((s) => s.smartPosition);
  const setSmartPosition = useDockStore((s) => s.setSmartPosition);
  const activeAppId = useOpenAppsStore((s) => s.activeAppId);
  const [menuOpen, setMenuOpen] = useState(false);

  if (position !== "appbar" || widgets.length === 0) return null;

  return (
    <DockLauncher
      compact
      widgets={widgets}
      openPanels={openPanels}
      onToggle={togglePanel}
      menuOpen={menuOpen}
      onMenuOpenChange={setMenuOpen}
      position={position}
      onPick={(next) => setPosition(next, activeAppId ?? null)}
      smartPosition={smartPosition}
      onToggleSmart={() => setSmartPosition(!smartPosition)}
    />
  );
}
